import { memo, useCallback, useRef } from "react";
import { useAppQuoteContext } from "../../../../../../context/AppQuoteContext";

import { Add, DragIndicator, ExpandLess, ExpandMore, Tune } from "@mui/icons-material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import WindowOutlinedIcon from "@mui/icons-material/WindowOutlined";
import {
	Box,
	Checkbox,
	CircularProgress,
	Collapse,
	FormControlLabel,
	IconButton,
	InputAdornment,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
	styled
} from "@mui/material";

import TruncatedText from "../../../forms/truncatedText";
import { useEditorContext } from "../context/editorContext";
import ReactFloatingScroll from "../FloatingScroll/FloatingScroll";
import useDraggableExpandableContainer from "./draggableExpandableContainer";
import useStyles from "./styles";

const StyledContent = styled(Box)(({ theme }) => ({
	marginTop: 0,
	padding: theme.spacing(2),
	backgroundColor: theme.palette.background.default,
	borderRadius: theme.shape.borderRadius,
	textOverflow: "ellipsis",
	overflow: "hidden"
}));

function DraggableExpandable({
	id,
	tabItem,
	onRemove,
	onMove,
	open,
	duplicatedTabId,
	updateOpenTab,
	setDuplicatedTabId,
	setChange,
	content,
	expanded,
	openCustomizeGrid,
	setLoading
}) {
	const {
		anchorEl,
		contentGrid,
		darkMode,
		disabledAddNewTab,
		drag,
		drop,
		duplicateTab,
		duplicateTabAsRadioOption,
		getTabNumber,
		getTabColor,
		gridRows,
		handleCheckBoxClick,
		handleClick,
		handleEditTab,
		handleKeyUp,
		handleMenuClick,
		handleMenuClose,
		handleOnBlur,
		handleRenameTab,
		handleSelectNewTab,
		handleSheetMenuClick,
		handleSheetMenuClose,
		handleUpdateAllCosts,
		isActive,
		isDragging,
		isOptional,
		isPrinted,
		isSpreadsheetRefreshing,
		isStandardUser,
		isStandardUserAndProtectedTab,
		isStandardUserPlus,
		isTotalsIncluded,
		listClasses,
		optionGroupLabel,
		orderPorterRequired,
		removeZeroQtyItems,
		renameTab,
		setTabName,
		sheetAnchorEl,
		showShortMenu,
		tabGroupLabel,
		tabName,
		textInput,
		updateSpreadsheet
	} = useDraggableExpandableContainer(
		id,
		tabItem,
		onMove,
		open,
		duplicatedTabId,
		updateOpenTab,
		setDuplicatedTabId,
		setChange,
		setLoading
	);
	const {quote} = useAppQuoteContext();
	const currentRef = useRef();
	const { 
		isDisabledQuoteActions,
		selectedTabs,
		shouldDisplayTabNumbers	} = useEditorContext();

	const isMenuItemDisabled = disabledAddNewTab || isDisabledQuoteActions;
	const getItemsByTab = quosal.util.getItemsByTabId(quote);
	let tabHasProtectedItems = getItemsByTab[tabItem?.IdQuoteTabs]?.some((item) => item.IsProtectedItem) || false;
	let tabIsProtected = tabItem?.IsProtectedTab || false;
	if (quosal.util.userCanModifyProtectedTab()) {
		tabHasProtectedItems = false;
		tabIsProtected = false;
	}

	const {
		listItemTextStyle,
		dragIndicatorStyle,
		textFieldStyle,
		menuCheckboxStyle,
		elementsColor
	} = useStyles();

	const handleFewTabsDragContext = (node) => {
		if (isStandardUserAndProtectedTab) {
			return;
		}
		const selectedTabIds = selectedTabs.map((tab) => tab.tabId);
		if (selectedTabIds !== null && selectedTabIds.length > 1) {
			if (selectedTabIds !== null && selectedTabIds.length > 1) {
				return drag(drop(node));
			}
			return null;
		}
	};

	return (
		<Box>
			<ListItem
				className={listClasses}
				ref={(node) => drag(drop(node))}
				style={{
					opacity: isDragging || isActive ? 0.5 : 1,
					textOverflow: "ellipsis",
					overflow: "hidden",
					marginBottom: open == id ? 0 : undefined,
					borderRadius: open == id ? "10px 10px 0 0" : undefined
				}}
			>
				<Box
					display="flex"
					alignItems="center"
					width="100%">
					<IconButton
						size="small"
						ref={(node) => handleFewTabsDragContext(node)}
						edge="start"
						onClick={handleClick}
						disabled={disabledAddNewTab || isStandardUserAndProtectedTab}
						style={{ ...dragIndicatorStyle, opacity: isDragging || isActive ? 0.5 : 1 }}
					>
						{!disabledAddNewTab && !isStandardUserAndProtectedTab && (
							<DragIndicator />
						)}
					</IconButton>
					{(duplicatedTabId == id) || (renameTab && open == id) ? (
						<TextField
							draggable
							onDragStart={(event) => event.preventDefault()}
							inputRef={textInput}
							onBlur={handleOnBlur}
							label="Name"
							onKeyUp={handleKeyUp}
							variant="standard"
							value={tabName}
							onChange={(e) => setTabName(e.target.value)}
							InputLabelProps={{ style: elementsColor }}
							sx={{ ...textFieldStyle }}
						/>
					) : (
						(disabledAddNewTab ? (
							<ListItemText
								sx={{ color: darkMode ? "#FFFFFF" : "#000000" }}
								primary={tabName}
								primaryTypographyProps={{
									...listItemTextStyle
								}}
							/>
					
						) : (
							<>
								<ListItemText
									sx={{
										fontStyle: isOptional ? "italic" : "normal",
										cursor: "pointer",
										color: darkMode ? "#FFFFFF" : "#000000",
									}}
									onClick={(e) => {
										handleClick();
									}
									}
									primary={
										<Box sx={{
											display: 'flex',
											alignItems: 'flex-start',
											justifyContent: 'flex-start',
											marginLeft: '-3px'
										}}>
											{shouldDisplayTabNumbers && (
												<InputAdornment
													position="start"
													variant="standard"
													sx={{
														"height": "100%",
													}}
												>
													<Typography
														sx={{
															"margin": "auto",
															color: darkMode ? (getTabColor() === "" || getTabColor() === "black") ? "#FFFFFF" : "#000000" : "#000000",
															fontWeight: "400"
														}}>
														{getTabNumber(tabItem) + ")"}
													</Typography>
												</InputAdornment>
											)}
											<TruncatedText
												text={tabName}
												tooltipTitle={tabName}
												showTooltip={true}
												maxWidth="100%"
												className="tabName"
												darkModeTextColor={getTabColor() === "" || getTabColor() === "black" ? "#FFFFFF" : "#000000"}
											/>
										</Box>
									}
								/>
								{tabGroupLabel}
								{optionGroupLabel}
							</>
						))
					)}

					<div className="tabActions">
						<ListItemSecondaryAction sx={{ display: "flex" }}>
							{tabItem?.HasSpreadsheet && (
								<div className="spreadsheetMenu">
									<IconButton
										size="small"
										edge="end"
										disabled={disabledAddNewTab || app.currentUser.IsReadOnly}
										onClick={handleSheetMenuClick}
									>
										<WindowOutlinedIcon />
									</IconButton>
									<Menu
										id="action-menu"
										anchorEl={sheetAnchorEl}
										keepMounted
										open={Boolean(sheetAnchorEl)}
										onClose={handleSheetMenuClose}
										disableScrollLock
										anchorOrigin={{
											vertical: "bottom",
											horizontal: !expanded ? "right" : "left"
										}}
										transformOrigin={{
											vertical: "top",
											horizontal: !expanded ? "left" : "right"
										}}
									>
										<MenuItem
											onClick={() => contentGrid.launchSpreadsheet(id)}>
											Launch Spreadsheet
										</MenuItem>
										{!isSpreadsheetRefreshing ? (
											<MenuItem
												onClick={updateSpreadsheet}>Refresh</MenuItem>
										) : (
											<MenuItem>
												<div style={{ marginRight: "5px" }}>
													Refreshing
												</div>
												<CircularProgress size={20} />
											</MenuItem>
										)}
									</Menu>
								</div >)}
								{disabledAddNewTab ? (
									<IconButton
										size="small"
										edge="end"
										color="primary"
										onClick={handleSelectNewTab}
										disabled={isDisabledQuoteActions}
										sx={{ ...elementsColor }}
									>
										<Add />
									</IconButton>
								) : (
									<>
										<Tooltip
											title="Tab Actions">
											<span>
												<IconButton
													aria-controls="action-menu"
													aria-haspopup="true"
													size="small"
													edge="end"
													onClick={handleMenuClick}
												>
													<Tune />
												</IconButton>
											</span>
										</Tooltip>
										{showShortMenu ? (
											<Menu
												id="action-menu"
												anchorEl={anchorEl}
												keepMounted
												open={Boolean(anchorEl)}
												onClose={handleMenuClose}
												disableScrollLock
												anchorOrigin={{
													vertical: "bottom",
													horizontal: !expanded ? "right" : "left"
												}}
												transformOrigin={{
													vertical: "top",
													horizontal: !expanded ? "left" : "right"
												}}
											>
												<MenuItem
													disabled={isMenuItemDisabled}
													onClick={handleEditTab}>
													Tab Details
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}
													onClick={handleMenuClose}>
													Show Line Item Quick Filter
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled || tabHasProtectedItems || tabIsProtected}
													onClick={duplicateTab}>
													Duplicate Tab
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled || tabHasProtectedItems || tabIsProtected}
													onClick={duplicateTabAsRadioOption}>
													Duplicate Tab as Radio Option
												</MenuItem>
											</Menu>
										) : (
											<Menu
												id="action-menu"
												anchorEl={anchorEl}
												keepMounted
												open={Boolean(anchorEl)}
												onClose={handleMenuClose}
												disableScrollLock
												anchorOrigin={{
													vertical: "bottom",
													horizontal: !expanded ? "right" : "left"
												}}
												transformOrigin={{
													vertical: "top",
													horizontal: !expanded ? "left" : "right"
												}}
											>
												<MenuItem
													disabled={isMenuItemDisabled}
													onClick={handleRenameTab}>
													Rename Tab
												</MenuItem>
												{isStandardUser || isStandardUserPlus ? null : (
													<MenuItem
														disabled={isMenuItemDisabled}
														onClick={() => {
															handleMenuClose();
															openCustomizeGrid(tabItem?.IdQuoteTabs);
														}}
													>
														Edit Grid Layout
													</MenuItem>
												)}
												<MenuItem
													onClick={handleEditTab}>
													Edit Tab
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}
													onClick={() => {
														removeZeroQtyItems(tabItem?.IdQuoteTabs);
													}}
												>
													Remove 0 Quantity Items
												</MenuItem>
												<MenuItem
													disabled={!gridRows.length || isMenuItemDisabled}
													onClick={() => handleUpdateAllCosts(tabItem?.IdQuoteTabs)}
												>
													Update All Costs
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}>
													<FormControlLabel
														control={
															<Checkbox
																checked={isOptional}
																onChange={() =>
																	handleCheckBoxClick(
																		"IsOptional",
																		isOptional
																	)
																}
																sx={{ ...menuCheckboxStyle }}
															/>
														}
														label="Is Deselected Options"
													/>
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}>
													<FormControlLabel
														control={
															<Checkbox
																checked={isPrinted}
																onChange={() =>
																	handleCheckBoxClick("IsPrinted", isPrinted)
																}
																sx={{ ...menuCheckboxStyle }}
															/>
														}
														label="Is Printed"
													/>
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}>
													<FormControlLabel
														control={
															<Checkbox
																checked={isTotalsIncluded}
																onChange={() =>
																	handleCheckBoxClick(
																		"IsTotalsIncluded",
																		isTotalsIncluded
																	)
																}
																sx={{ ...menuCheckboxStyle }}
															/>
														}
														label="Is Totals Included"
													/>
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled}>
													<FormControlLabel
														control={
															<Checkbox
																checked={orderPorterRequired}
																onChange={() =>
																	handleCheckBoxClick(
																		"OrderPorterRequired",
																		orderPorterRequired
																	)
																}
																sx={{ ...menuCheckboxStyle }}
															/>
														}
														label="Option Locked"
													/>
												</MenuItem>
												{quosal.settings.getValue("canSpreadsheet") && quosal.validation.isPackageLevelAuthorized(app.packageLevels.premium) ? (
													<MenuItem
														disabled={isMenuItemDisabled}
														onClick={() => contentGrid.launchSpreadsheet(id)}>
														Tab Spreadsheet
													</MenuItem>
												) : null}
												<MenuItem
													disabled={isMenuItemDisabled || tabHasProtectedItems || tabIsProtected}
													onClick={duplicateTab}>
													Duplicate Tab
												</MenuItem>
												<MenuItem
													disabled={isMenuItemDisabled || tabHasProtectedItems || tabIsProtected}
													onClick={duplicateTabAsRadioOption}>
													Duplicate Tab as Radio Option
												</MenuItem>
											</Menu>
										)}
										<IconButton
											size="small"
											edge="end"
											onClick={() => onRemove(true, id)}
											disabled={isMenuItemDisabled}
										>
											<DeleteOutlineOutlinedIcon />
										</IconButton>
										<IconButton
											size="small"
											edge="end"
											onClick={handleClick}>
											{open == id ? <ExpandLess /> : <ExpandMore />}
										</IconButton>
									</>
								)
							}
						</ListItemSecondaryAction>
					</div>
				</Box>
			</ListItem>
			<Collapse in={open == id} timeout="auto" unmountOnExit>
				<ReactFloatingScroll contentRef={currentRef} innerScrollableClass="MuiDataGrid-virtualScroller">
					<StyledContent ref={currentRef}>
						{content}</StyledContent>
				</ReactFloatingScroll>
			</Collapse>
		</Box >
	);
};

export default memo(DraggableExpandable);
